import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import Tooltip from '../../../components/ui/Tooltip';

const monthNames = {
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  kk: ['Қаңтар', 'Ақпан', 'Наурыз', 'Сәуір', 'Мамыр', 'Маусым', 'Шілде', 'Тамыз', 'Қыркүйек', 'Қазан', 'Қараша', 'Желтоқсан'],
  az: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'],
};

const dayHeaders = {
  tr: ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  kk: ['Дс', 'Сс', 'Ср', 'Бс', 'Жм', 'Сн', 'Жс'],
  az: ['B.e', 'Ç.a', 'Çər', 'C.a', 'Cüm', 'Şən', 'Baz'],
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const buildCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPadding = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = startPadding - 1; i >= 0; i--)
    days.push({ date: new Date(year, month, -i), isCurrentMonth: false });
  for (let d = 1; d <= lastDay.getDate(); d++)
    days.push({ date: new Date(year, month, d), isCurrentMonth: true });
  let nextDay = 1;
  while (days.length % 7 !== 0)
    days.push({ date: new Date(year, month + 1, nextDay++), isCurrentMonth: false });
  return days;
};

export default function EmployeeScheduleTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [scheduleMap, setScheduleMap] = useState(new Map());
  const [loading, setLoading] = useState(false);

  const calendarDays = useMemo(() => buildCalendarDays(year, month), [year, month]);

  const fetchSchedule = useCallback(async () => {
    const firstDate = calendarDays[0].date;
    const lastDate = calendarDays[calendarDays.length - 1].date;
    setLoading(true);
    try {
      // Uses plan-view endpoint — for white collar with a WorkPlan, empty cells get
      // filled with the default work calendar (source='plan'); for blue collar, only
      // published shift records are returned.
      const res = await api.get(
        `/v1/shift-schedules/user/${employeeId}/plan-view`,
        { params: { startDate: formatDate(firstDate), endDate: formatDate(lastDate) } }
      );
      const map = new Map();
      const items = res?.data?.data || [];
      items.forEach((item) => {
        const key = item.date?.substring(0, 10);
        if (key) map.set(key, item);
      });
      setScheduleMap(map);
    } catch {
      setScheduleMap(new Map());
    } finally {
      setLoading(false);
    }
  }, [calendarDays, employeeId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handlePrev = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const handleNext = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const lang = i18n.language?.substring(0, 2) || 'tr';
  const months = monthNames[lang] || monthNames.tr;
  const headers = dayHeaders[lang] || dayHeaders.tr;
  const todayStr = formatDate(today);

  const getCellInfo = (day) => {
    const key = formatDate(day.date);
    const entry = scheduleMap.get(key);
    const isToday = key === todayStr;
    const dayOfWeek = day.date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    let bg = '#fff';
    let content = null;
    let tooltipText = null;

    if (entry) {
      if (entry.isHoliday) {
        bg = '#fef9c3';
        content = <HolidayText>{entry.holidayName}</HolidayText>;
        tooltipText = entry.holidayName;
      } else if (entry.workHourId) {
        bg = (entry.workHourColorCode || '#0291EF') + '20';
        content = (
          <>
            <ShortCode $color={entry.workHourColorCode}>{entry.workHourShortCode}</ShortCode>
            <WorkHourName>{entry.workHourName}</WorkHourName>
          </>
        );
        const start = entry.workHourStart?.substring(0, 5) || '';
        const end = entry.workHourEnd?.substring(0, 5) || '';
        tooltipText = `${entry.workHourName}\n${start} - ${end}`;
      } else {
        bg = isWeekend ? '#f8fafc' : '#f1f5f9';
        content = <OffText>OFF</OffText>;
      }
    } else {
      bg = isWeekend ? '#f8fafc' : '#f1f5f9';
      if (day.isCurrentMonth && !loading) {
        content = <OffText>OFF</OffText>;
      }
    }

    return { bg, content, isToday, tooltipText };
  };

  return (
    <Container>
      <Header>
        <NavSection>
          <NavButton onClick={handlePrev}>&#9664;</NavButton>
          <MonthLabel>{months[month]} {year}</MonthLabel>
          <NavButton onClick={handleNext}>&#9654;</NavButton>
        </NavSection>
        {/* PARTİ 19 — beş dil SATIR İÇİ TERNARY ile taşınıyordu ve Türkçesi
            "Bugun" (ü eksik) yazıyordu. Metin sözlüğe taşındı; yeni bir dil
            eklendiğinde bu düğme İngilizceye düşmez ve yazım hatası tek
            yerden düzelir. */}
        <TodayButton onClick={handleToday}>{t('common.today')}</TodayButton>
      </Header>

      <CalendarGrid>
        {headers.map((dh) => (
          <DayHeader key={dh}>{dh}</DayHeader>
        ))}

        {calendarDays.map((day) => {
          const key = formatDate(day.date);
          const { bg, content, isToday, tooltipText } = getCellInfo(day);

          const cell = (
            <DayCell
              key={key}
              $bg={bg}
              $outOfMonth={!day.isCurrentMonth}
              $isToday={isToday}
            >
              <DayNumber $isToday={isToday}>{day.date.getDate()}</DayNumber>
              {content}
            </DayCell>
          );

          if (tooltipText && day.isCurrentMonth) {
            return (
              <Tooltip key={key} text={tooltipText} position="bottom">
                {cell}
              </Tooltip>
            );
          }

          return cell;
        })}
      </CalendarGrid>
    </Container>
  );
}

const Container = styled.div`
  padding: 8px 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid ${p => p.theme.colors?.border || '#E8ECF0'};
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  color: ${p => p.theme.colors?.text?.primary || '#004242'};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${p => p.theme.colors?.bgColor || '#F5F7FA'};
  }
`;

const MonthLabel = styled.span`
  font-size: ${p => p.theme.metrics?.font?.size?.lg || '18px'};
  font-weight: ${p => p.theme.metrics?.font?.weight?.bold || 700};
  color: ${p => p.theme.colors?.text?.primary || '#004242'};
  min-width: 180px;
  text-align: center;
`;

const TodayButton = styled.button`
  background: ${p => p.theme.colors?.primary?.main || '#0291EF'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: ${p => p.theme.metrics?.font?.size?.sm || '14px'};
  font-weight: ${p => p.theme.metrics?.font?.weight?.semiBold || 600};
  font-family: ${p => p.theme.metrics?.font?.family || "'Nunito Sans', sans-serif"};

  &:hover {
    opacity: 0.9;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #E8ECF0;
  border-radius: 6px;
  overflow: hidden;
`;

const DayHeader = styled.div`
  background: ${p => p.theme.colors?.bgColor || '#F5F7FA'};
  padding: 10px;
  text-align: center;
  font-size: ${p => p.theme.metrics?.font?.size?.sm || '13px'};
  font-weight: ${p => p.theme.metrics?.font?.weight?.semiBold || 600};
  color: ${p => p.theme.colors?.text?.secondary || '#64748b'};
`;

const DayCell = styled.div`
  background: ${p => p.$bg || '#fff'};
  min-height: 90px;
  padding: 8px;
  opacity: ${p => (p.$outOfMonth ? 0.35 : 1)};
  border: ${p => (p.$isToday ? '2px solid #0291EF' : 'none')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const DayNumber = styled.span`
  font-size: 13px;
  font-weight: ${p => (p.$isToday ? 700 : 400)};
  color: ${p => (p.$isToday ? '#EF4444' : '#334155')};
  align-self: flex-end;
`;

const ShortCode = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.$color || '#0291EF'};
  margin-top: 4px;
`;

const WorkHourName = styled.span`
  font-size: 11px;
  color: #64748b;
  text-align: center;
  line-height: 1.2;
`;

const OffText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
  margin-top: 8px;
`;

const HolidayText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  text-align: center;
  margin-top: 4px;
  line-height: 1.2;
`;
